/**
 * Configuration Cache Service
 *
 * In-memory cache for configuration data with automatic expiry.
 *
 * Features:
 * - 5-minute default TTL
 * - Automatic cleanup of expired entries
 * - Type-safe operations
 * - Memory efficient
 */

import { createLogger } from '../config/environment';

const logger = createLogger('ConfigCache');

interface CacheEntry<T> {
  data: T;
  expiresAt: number;
}

class ConfigurationCache {
  private cache: Map<string, CacheEntry<any>>;
  private defaultTTL: number = 5 * 60 * 1000; // 5 minutes in milliseconds
  private cleanupInterval: NodeJS.Timeout | null = null;

  constructor() {
    this.cache = new Map();
    this.startCleanup();
  }

  /**
   * Get cached value if not expired
   */
  get<T>(key: string): T | null {
    const entry = this.cache.get(key);

    if (!entry) {
      logger.debug(`Miss: ${key}`);
      return null;
    }

    if (Date.now() > entry.expiresAt) {
      logger.debug(`Expired: ${key}`);
      this.cache.delete(key);
      return null;
    }

    logger.debug(`✅ Hit: ${key}`);
    return entry.data as T;
  }

  /**
   * Set value in cache with TTL
   */
  set<T>(key: string, data: T, ttl?: number): void {
    const expiryTime = ttl || this.defaultTTL;
    const expiresAt = Date.now() + expiryTime;

    this.cache.set(key, { data, expiresAt });
    logger.debug(`✅ Stored: ${key} (expires in ${expiryTime / 1000}s)`);
  }

  /**
   * Delete specific key from cache
   */
  delete(key: string): boolean {
    const deleted = this.cache.delete(key);
    if (deleted) {
      logger.debug(`🗑️ Deleted: ${key}`);
    }
    return deleted;
  }

  /**
   * Clear all cache entries
   */
  clear(): void {
    const count = this.cache.size;
    this.cache.clear();
    logger.info(`🧹 Cleared all cache (${count} entries)`);
  }

  /**
   * Get cache statistics
   */
  getStats(): { size: number; keys: string[] } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
    };
  }

  /**
   * Start automatic cleanup of expired entries
   */
  private startCleanup(): void {
    // Run cleanup every minute
    this.cleanupInterval = setInterval(() => {
      this.cleanupExpired();
    }, 60 * 1000);

    logger.debug('🧹 Automatic cleanup started (every 60s)');
  }

  /**
   * Clean up expired cache entries
   */
  private cleanupExpired(): void {
    const now = Date.now();
    let cleaned = 0;

    for (const [key, entry] of this.cache.entries()) {
      if (now > entry.expiresAt) {
        this.cache.delete(key);
        cleaned++;
      }
    }

    if (cleaned > 0) {
      logger.debug(`🧹 Cleaned ${cleaned} expired entries`);
    }
  }

  /**
   * Stop automatic cleanup (for testing/cleanup)
   */
  stopCleanup(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
      logger.info('Automatic cleanup stopped');
    }
  }
}

// Singleton instance
export const configCache = new ConfigurationCache();

// Export class for testing
export { ConfigurationCache };

